import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CONFIG } from 'constants/constants';
import { BUTTON_BORDER_RADIUS } from 'constants/borderRadius';

/**
 * @typedef {object} ErrorDisplayProps
 * @property {string} message - Error message to display
 * @property {Function} [onRetry] - Callback for retry action (if recoverable)
 * @property {boolean} [canRetry=false] - Whether error is recoverable
 * @property {Function} [onGoHome] - Optional callback for "Return" action (allows parent to sign out first)
 */

/**
 * Error display component with retry functionality.
 * Ensures users always have a path forward (NFR-3.4.2).
 * Uses subtle styling since the LoginCard shows error state with red liquid glass.
 *
 * @param {ErrorDisplayProps} props - Component props
 * @returns {React.ReactElement}
 */
const ErrorDisplay = ({ message, onRetry, canRetry = false, onGoHome }) => {
    /**
     * Redirects to the default application URL
     */
    const handleGoHome = () => {
        if (onGoHome) {
            onGoHome();
        } else {
            console.log(CONFIG.DEFAULT_APP_URL);
            window.location.href = CONFIG.DEFAULT_APP_URL;
        }
    };


    return (
        <Box sx={{ mt: 2 }}>
            {/* Subtle error message with icon */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mb: 3,
                }}
            >
                <ErrorOutlineIcon
                    color="error"
                    sx={{ mt: 0.25, fontSize: '1.25rem' }}
                />
                <Typography
                    variant="body2"
                    color="error"
                    sx={{ flex: 1, lineHeight: 1.6 }}
                >
                    {message}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {canRetry && onRetry && (
                    <Button
                        id="error-retry-btn"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={onRetry}
                        startIcon={<RefreshIcon />}
                        sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                    >
                        Try Again
                    </Button>
                )}

                <Button
                    id="error-home-btn"
                    variant={canRetry ? 'outlined' : 'contained'}
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={handleGoHome}
                    startIcon={<HomeIcon />}
                    sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                >
                    Return
                </Button>
            </Box>
        </Box>
    );
};

ErrorDisplay.propTypes = {
    message: PropTypes.string.isRequired,
    onRetry: PropTypes.func,
    canRetry: PropTypes.bool,
    onGoHome: PropTypes.func,
};

export default ErrorDisplay;
